import { Divider } from '@mui/material';
import { ReactComponent as TableNextSvg } from 'assets/svg/Table-Next.svg';
import styled from 'styled-components';

interface PropertyPaginationProps {
    handlePageChange: (page: number) => void;
    half?: boolean;
    totalPageCount?: number;
    currentPage?: number;
}

const PageItemDiv = styled.div`
    width: 40px;
    height: 40px;
    line-height: 40px;
    transition: all 0.2s ease-in-out;
    border-radius: 8px;
    font-weight: 500;
    color: #666;
    margin: 0 2px;

    &:hover {
        background-color: rgba(49, 118, 188, 0.1);
        color: #3176bc;
    }

    &.selected {
        background-color: #3176bc !important;
        color: white !important;

        &:hover {
            background-color: #3176bc !important;
            color: white !important;
        }
    }

    @media (max-width: 500px) {
        width: 30px;
    }
`;

const PaginationContainer = styled.div`
    padding: 12px;
    .pagination-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: white;
        border-radius: 12px;
    }
`;

const NavigationButton = styled.div<{ disabled?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 41px;
    padding: 10px 35px;
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    opacity: ${(props) => (props.disabled ? 0.5 : 1)};
    transition: all 0.2s ease-in-out;
    border-radius: 8px;
    font-weight: 500;
    color: #666;
    margin: 0 4px;

    &:hover {
        background-color: ${(props) => (props.disabled ? 'transparent' : 'rgba(49, 118, 188, 0.1)')};
        color: ${(props) => (props.disabled ? '#666' : '#3176BC')};
    }

    @media (max-width: 768px) {
        padding: 10px 12px;
        .button-text {
            display: none;
        }
    }
`;

const NextButton = ({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) => {
    return (
        <NavigationButton onClick={onClick} disabled={disabled} data-cy="property-pagination-next">
            <span className="button-text font-medium leading-[21px] normal-case">Next</span>
            <TableNextSvg />
        </NavigationButton>
    );
};

const PrevButton = ({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) => {
    return (
        <NavigationButton onClick={onClick} disabled={disabled} data-cy="property-pagination-prev">
            <TableNextSvg className="rotate-180" />
            <span className="button-text font-medium leading-[21px] normal-case">Prev</span>
        </NavigationButton>
    );
};

const PageItem = ({
    title,
    sep,
    selected = false,
    onClick,
    'data-cy': dataCy,
}: {
    title: string;
    sep: boolean;
    selected?: boolean;
    onClick?: () => void;
    'data-cy'?: string;
}) => {
    return (
        <>
            <PageItemDiv className={`relative text-center cursor-pointer ${selected ? 'selected' : ''}`} onClick={onClick} data-cy={dataCy}>
                {title}
            </PageItemDiv>
            {sep && <Divider orientation="vertical" className="!h-[38px] w-[1px] !bg-gray-200" />}
        </>
    );
};

const PropertyPagination: React.FC<PropertyPaginationProps> = ({ handlePageChange: onPageChanged, half = false, currentPage = 0, totalPageCount = 0 }) => {
    // Don't render pagination if there's only one page or no pages
    if (totalPageCount <= 1) {
        return null;
    }

    const isPrevButtonVisible = currentPage > 0;
    const isNextButtonVisible = currentPage < totalPageCount - 1;

    const onNext = () => {
        const nextpage = currentPage + 1;
        if (nextpage < totalPageCount && nextpage >= 0) handlePageChange(nextpage);
    };

    const onPrev = () => {
        const nextpage = currentPage - 1;
        if (nextpage < totalPageCount && nextpage >= 0) handlePageChange(nextpage);
    };

    const handlePageChange = (page: number) => {
        if (page < totalPageCount && page >= 0) {
            onPageChanged(page);
        }
    };

    const renderPageNumbers = () => {
        const pages: JSX.Element[] = [];
        const isMobile = window.innerWidth <= 768;
        const nextPagesCount = isMobile ? 2 : 4; // Show 2 pages on mobile, 4 on desktop

        // Helper function to add page number
        const addPage = (pageNumber: number) => {
            pages.push(
                <PageItem
                    key={pageNumber}
                    title={`${pageNumber + 1}`}
                    sep={true}
                    onClick={() => handlePageChange(pageNumber)}
                    selected={currentPage === pageNumber}
                    data-cy={`property-pagination-item-${pageNumber}`}
                />,
            );
        };

        // Helper function to add ellipsis
        const addEllipsis = (key: string) => {
            pages.push(<PageItem key={key} title="..." sep={true} data-cy={`property-pagination-ellipsis-${key}`} />);
        };

        // Always add first page (unless we're already on it)
        if (currentPage !== 0) {
            addPage(0);
        }

        // If we're on the first page, show current page and next pages
        if (currentPage === 0) {
            // Add current page (first page)
            addPage(currentPage);

            // Add next pages
            for (let i = 1; i <= nextPagesCount; i++) {
                const nextPage = currentPage + i;
                if (nextPage < totalPageCount - 1) {
                    addPage(nextPage);
                }
            }

            // Add ellipsis if there's a gap
            if (currentPage + nextPagesCount < totalPageCount - 1) {
                addEllipsis('middle');
            }

            // Add last page
            const lastPage = totalPageCount - 1;
            if (lastPage > currentPage + nextPagesCount) {
                addPage(lastPage);
            }
        } else if (currentPage === totalPageCount - 1) {
            // If we're on the last page, show first page and previous pages
            // First page is already added above

            // Add ellipsis if there's a gap
            if (currentPage - nextPagesCount > 0) {
                addEllipsis('start');
            }

            // Add previous pages
            for (let i = nextPagesCount; i > 0; i--) {
                const prevPage = currentPage - i;
                if (prevPage > 0) {
                    addPage(prevPage);
                }
            }

            // Add current page (last page)
            addPage(currentPage);
        } else {
            // We're on a middle page, show first page, current page, and surrounding pages
            // First page is already added above

            // Add ellipsis if there's a gap between first page and current page
            if (currentPage - nextPagesCount > 0) {
                addEllipsis('start');
            }

            // Add previous pages (but not if they would overlap with first page)
            for (let i = nextPagesCount; i > 0; i--) {
                const prevPage = currentPage - i;
                if (prevPage > 0) {
                    addPage(prevPage);
                }
            }

            // Add current page
            addPage(currentPage);

            // Add next pages
            for (let i = 1; i <= nextPagesCount; i++) {
                const nextPage = currentPage + i;
                if (nextPage < totalPageCount - 1) {
                    addPage(nextPage);
                }
            }

            // Add ellipsis if there's a gap between current page and last page
            if (currentPage + nextPagesCount < totalPageCount - 1) {
                addEllipsis('middle');
            }

            // Add last page
            const lastPage = totalPageCount - 1;
            if (lastPage > currentPage + nextPagesCount) {
                addPage(lastPage);
            }
        }

        return pages;
    };

    return (
        <PaginationContainer>
            <div className="pagination-wrapper">
                {isPrevButtonVisible && <PrevButton onClick={onPrev} />}
                {isPrevButtonVisible && <Divider orientation="vertical" className="!h-[38px] w-[1px] !bg-gray-200" />}

                {renderPageNumbers()}

                {isNextButtonVisible && <Divider orientation="vertical" className="!h-[38px] w-[1px] !bg-gray-200" />}
                {isNextButtonVisible && <NextButton onClick={onNext} />}
            </div>
        </PaginationContainer>
    );
};

export default PropertyPagination;
